import React, { useEffect, useState } from 'react';
import { ArrowLeft, AlertTriangle, RefreshCw, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * USDT 출금 화면 — BSC(BEP-20) / 트론(TRC-20) (2026-09-14 신규)
 *
 * 입금 화면과 짝이다. 같은 규칙을 따른다.
 *  · 네트워크를 먼저 고르게 하고, 주소 형식을 그 자리에서 검사한다.
 *    (BSC 주소를 트론으로 보내면 돈이 사라진다)
 *  · 연결·인증된 외부지갑이 있으면 그 목록에서 고르게 한다. 손으로 치다 한 글자
 *    틀리는 사고가 제일 흔하다.
 *  · 수수료를 뺀 "실제 받을 금액"을 보내기 전에 크게 보여준다.
 */

interface Props {
  onBack: () => void;
}

type Net = 'bsc' | 'tron';

interface Info {
  enabled: boolean;
  balance: number;
  minAmount: number;
  fees: Record<string, number>;
  networks: { network: Net; label: string; fee: number }[];
  linkedWallets: { address: string; wallet_type: string | null }[];
}

interface Row {
  id: number;
  network: string;
  to_address: string;
  amount_usdt: string;
  fee_usdt: string;
  send_amount: string;
  status: string;
  tx_hash: string | null;
  reject_reason: string | null;
  requested_at: string;
}

const STATUS: Record<string, { text: string; color: string; bg: string }> = {
  requested: { text: '승인 대기', color: '#B45309', bg: '#FEF3C7' },
  approved:  { text: '승인됨',   color: '#1D4ED8', bg: '#DBEAFE' },
  sending:   { text: '전송 중',  color: '#1D4ED8', bg: '#DBEAFE' },
  completed: { text: '출금 완료', color: '#15803D', bg: '#DCFCE7' },
  rejected:  { text: '거절됨',   color: '#6B7280', bg: '#F3F4F6' },
  failed:    { text: '실패',     color: '#B91C1C', bg: '#FEE2E2' },
};

const NET_LABEL: Record<Net, { badge: string; bg: string; fg: string; hint: string }> = {
  bsc:  { badge: 'BEP-20', bg: '#FEF3C7', fg: '#B45309', hint: '0x 로 시작하는 주소' },
  tron: { badge: 'TRC-20', bg: '#FEE2E2', fg: '#B91C1C', hint: 'T 로 시작하는 주소' },
};

export function UsdtWithdraw({ onBack }: Props) {
  const [info, setInfo] = useState<Info | null>(null);
  const [net, setNet] = useState<Net>('bsc');
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('handy-jwt-token') || '';
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = async () => {
    try {
      const r = await fetch('/api/withdraw/info', { headers: authHeader() as any });
      const d = await r.json();
      if (d.success) setInfo(d);
    } catch { /* 화면을 막지 않는다 */ }
    try {
      const r = await fetch('/api/withdraw/history', { headers: authHeader() as any });
      const d = await r.json();
      if (d.success) setRows(d.withdrawals || []);
    } catch { /* 무시 */ }
  };

  useEffect(() => { load(); }, []);

  const fee = info?.fees?.[net] ?? 0;
  const amt = Number(amount || 0);
  const receive = Math.max(0, Number((amt - fee).toFixed(6)));
  const balance = info?.balance ?? 0;

  // 주소 형식은 보내기 전에 잡는다. 틀린 체인으로 보내면 복구가 안 된다.
  const addrOk = net === 'tron'
    ? /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address.trim())
    : /^0x[0-9a-fA-F]{40}$/.test(address.trim());

  const canSubmit = Boolean(info?.enabled) && addrOk && amt >= (info?.minAmount ?? 0)
    && amt <= balance && receive > 0 && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch('/api/withdraw/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeader() as any) },
        body: JSON.stringify({ network: net, toAddress: address.trim(), amount: amt }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || '출금 요청에 실패했습니다');
      toast.success('출금 요청이 접수되었습니다.');
      setAmount('');
      setAddress('');
      setConfirm(false);
      await load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id: number) => {
    try {
      const r = await fetch(`/api/withdraw/${id}/cancel`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...(authHeader() as any) },
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || '취소할 수 없습니다');
      toast.success('요청을 취소했습니다.');
      await load();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const cfg = NET_LABEL[net];

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center gap-3 px-4 py-4">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center rounded-xl bg-muted">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-foreground">USDT 출금</h1>
      </div>

      <div className="px-4 pb-24 space-y-4">
        {/* 잔액 */}
        <div className="rounded-2xl p-5" style={{ background: '#111827' }}>
          <p className="text-xs" style={{ color: '#9CA3AF' }}>출금 가능 금액</p>
          <p className="text-2xl font-bold mt-1" style={{ color: '#FFFFFF' }}>
            {balance.toLocaleString(undefined, { maximumFractionDigits: 6 })} USDT
          </p>
        </div>

        {/* 네트워크 선택 */}
        <div className="flex gap-1 p-1 rounded-2xl" style={{ background: '#F3F4F6', border: '1px solid #E5E7EB' }}>
          {(info?.networks || [{ network: 'bsc' as Net, label: 'BNB 체인', fee: 0 },
                               { network: 'tron' as Net, label: '트론', fee: 0 }]).map(n => {
            const on = n.network === net;
            const c = NET_LABEL[n.network];
            return (
              <button
                key={n.network}
                onClick={() => { setNet(n.network); setAddress(''); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{
                  background: on ? '#FFFFFF' : 'transparent',
                  color: on ? '#111827' : '#9CA3AF',
                  boxShadow: on ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {n.network === 'tron' ? '트론' : 'BNB 체인'}
                <span className="ml-1.5 text-[10px] font-bold" style={{ color: on ? c.fg : '#C4C7CC' }}>
                  {c.badge}
                </span>
              </button>
            );
          })}
        </div>

        {/* 경고 */}
        <div className="rounded-2xl p-4" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
          <div className="flex gap-2.5">
            <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: '#DC2626' }} />
            <p className="text-xs leading-relaxed" style={{ color: '#7F1D1D' }}>
              받는 주소가 <b>{cfg.badge}</b> 네트워크 주소인지 반드시 확인하세요.
              <br />다른 네트워크 주소로 보내면 <b>자산을 되찾을 수 없습니다.</b>
            </p>
          </div>
        </div>

        {/* 입력 */}
        <div className="rounded-2xl p-5 space-y-4" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-foreground">받는 주소</span>
              <span className="text-[11px]" style={{ color: '#9CA3AF' }}>{cfg.hint}</span>
            </div>

            {(info?.linkedWallets?.length ?? 0) > 0 && net === 'bsc' && (
              <div className="flex gap-2 flex-wrap mb-2">
                {info!.linkedWallets.map(w => (
                  <button
                    key={w.address}
                    onClick={() => setAddress(w.address)}
                    className="text-[11px] px-2.5 py-1.5 rounded-lg font-bold"
                    style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}
                  >
                    {w.wallet_type || '연결지갑'} {w.address.slice(0, 6)}…{w.address.slice(-4)}
                  </button>
                ))}
              </div>
            )}

            <input
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder={net === 'tron' ? 'T…' : '0x…'}
              className="w-full rounded-xl p-3 text-sm font-mono"
              style={{
                background: '#F9FAFB', color: '#111827',
                border: '1px solid ' + (address && !addrOk ? '#FECACA' : '#E5E7EB'),
              }}
            />
            {address && !addrOk && (
              <p className="text-[11px] mt-1.5" style={{ color: '#B91C1C' }}>
                {cfg.badge} 주소 형식이 아닙니다.
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-foreground">출금 금액</span>
              <button
                onClick={() => setAmount(String(balance))}
                className="text-[11px] font-bold" style={{ color: '#FF6B00' }}
              >
                전액
              </button>
            </div>
            <input
              value={amount}
              onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              inputMode="decimal"
              placeholder="0"
              className="w-full rounded-xl p-3 text-lg font-bold"
              style={{ background: '#F9FAFB', color: '#111827', border: '1px solid #E5E7EB' }}
            />
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-[12px]" style={{ color: '#6B7280' }}>
                <span>수수료</span><span>{fee} USDT</span>
              </div>
              <div className="flex justify-between text-sm font-bold" style={{ color: '#111827' }}>
                <span>실제 받는 금액</span>
                <span>{receive.toLocaleString(undefined, { maximumFractionDigits: 6 })} USDT</span>
              </div>
            </div>
            {amt > balance && (
              <p className="text-[11px] mt-1.5" style={{ color: '#B91C1C' }}>잔액을 초과했습니다.</p>
            )}
            {amt > 0 && amt < (info?.minAmount ?? 0) && (
              <p className="text-[11px] mt-1.5" style={{ color: '#B91C1C' }}>
                최소 {info?.minAmount} USDT 부터 출금할 수 있습니다.
              </p>
            )}
          </div>

          <button
            onClick={() => setConfirm(true)}
            disabled={!canSubmit}
            className="w-full py-3.5 rounded-xl font-bold"
            style={{
              background: canSubmit ? '#FF6B00' : '#E5E7EB',
              color: canSubmit ? '#FFFFFF' : '#9CA3AF',
            }}
          >
            출금 요청
          </button>
        </div>

        {/* 내역 */}
        <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-foreground">출금 내역</span>
            <button onClick={load} className="p-1.5 rounded-lg" style={{ background: '#F3F4F6' }}>
              <RefreshCw className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
            </button>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ color: '#9CA3AF' }}>아직 출금 내역이 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {rows.map(w => {
                const s = STATUS[w.status] || STATUS.rejected;
                return (
                  <div key={w.id} className="rounded-xl p-3" style={{ background: '#F9FAFB' }}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-foreground">
                          {Number(w.amount_usdt).toLocaleString(undefined, { maximumFractionDigits: 6 })} USDT
                          <span className="ml-1.5 text-[10px] font-bold" style={{ color: '#9CA3AF' }}>
                            {w.network === 'tron' ? 'TRC-20' : 'BEP-20'}
                          </span>
                        </p>
                        <p className="text-[11px] mt-0.5 font-mono break-all" style={{ color: '#9CA3AF' }}>
                          {w.to_address}
                        </p>
                        <p className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>
                          {new Date(w.requested_at).toLocaleString('ko-KR')}
                        </p>
                        {w.reject_reason && (
                          <p className="text-[11px] mt-1" style={{ color: '#B91C1C' }}>{w.reject_reason}</p>
                        )}
                      </div>
                      <span
                        className="text-[11px] px-2.5 py-1 rounded-full font-bold flex items-center gap-1 shrink-0"
                        style={{ background: s.bg, color: s.color }}
                      >
                        {w.status === 'completed' ? <CheckCircle2 className="w-3 h-3" />
                          : w.status === 'failed' ? <XCircle className="w-3 h-3" />
                          : <Clock className="w-3 h-3" />}
                        {s.text}
                      </span>
                    </div>
                    {w.status === 'requested' && (
                      <button
                        onClick={() => cancel(w.id)}
                        className="mt-2 text-[11px] font-bold"
                        style={{ color: '#6B7280' }}
                      >
                        요청 취소
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 확인 */}
      {confirm && (
        <div
          className="fixed inset-0 flex items-end justify-center p-3"
          style={{ background: 'rgba(0,0,0,0.55)', zIndex: 60 }}
        >
          <div className="w-full rounded-2xl p-5" style={{ background: '#FFFFFF', maxWidth: 440 }}>
            <h3 className="text-lg font-bold text-foreground">출금 확인</h3>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
              보낸 뒤에는 되돌릴 수 없습니다.
            </p>
            <div className="rounded-xl p-4 mt-4 space-y-2" style={{ background: '#F9FAFB' }}>
              <div className="flex justify-between text-[13px]">
                <span style={{ color: '#6B7280' }}>네트워크</span>
                <span className="font-bold" style={{ color: '#111827' }}>{cfg.badge}</span>
              </div>
              <div className="text-[13px]">
                <span style={{ color: '#6B7280' }}>받는 주소</span>
                <p className="font-mono break-all mt-1" style={{ color: '#111827' }}>{address}</p>
              </div>
              <div className="flex justify-between text-[13px]">
                <span style={{ color: '#6B7280' }}>출금 금액</span>
                <span className="font-bold" style={{ color: '#111827' }}>{amt} USDT</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span style={{ color: '#6B7280' }}>수수료</span>
                <span style={{ color: '#111827' }}>{fee} USDT</span>
              </div>
              <div className="flex justify-between text-base pt-1" style={{ borderTop: '1px solid #E5E7EB' }}>
                <span className="font-bold" style={{ color: '#111827' }}>실제 받는 금액</span>
                <span className="font-bold" style={{ color: '#DC2626' }}>{receive} USDT</span>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setConfirm(false)}
                disabled={busy}
                className="flex-1 py-3 rounded-xl font-bold"
                style={{ background: '#F3F4F6', color: '#374151' }}
              >
                취소
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="flex-1 py-3 rounded-xl font-bold"
                style={{ background: '#DC2626', color: '#FFFFFF', opacity: busy ? 0.6 : 1 }}
              >
                {busy ? '요청 중…' : '출금 요청'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
